import { useEffect } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import clsx from "clsx";
import { EmpresaSelector } from "./EmpresaSelector";
import { PageTransition } from "./PageTransition";
import { useAuth } from "@/contexts/AuthContext";
import { showToast } from "@/components/ui/Toast";
import { Logo } from "@/components/ui/Logo";
import { UserInfo } from "@/components/ui/UserInfo";

interface NavItem {
  to: string;
  label: string;
  requires?: string;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const dashboardGroup: NavGroup = {
  label: "Dashboard",
  items: [{ to: "/", label: "Inicio" }],
};

const consultasGroup: NavGroup = {
  label: "Consultas",
  items: [{ to: "/confecamaras", label: "NITs Confecámaras" }],
};

const herramientasGroup: NavGroup = {
  label: "Herramientas",
  items: [{ to: "/herramientas", label: "Centro de accesos" }],
};

export const AppLayout = () => {
  const { user, isAdmin, hasPermission } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (!user) return;
    let socket: WebSocket | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let retryDelay = 1000;
    let isCancelled = false;

    const connect = () => {
      if (isCancelled) return;
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const clientId = Date.now().toString() + "_notify";
      socket = new WebSocket(
        `${protocol}//${window.location.host}/ws/user/notifications/${clientId}`
      );

      socket.onopen = () => {
        retryDelay = 1000;
      };

      socket.onmessage = (event) => {
        try {
          const updates = JSON.parse(event.data);
          const items = Array.isArray(updates) ? updates : [updates];
          for (const item of items) {
            // Notificar a las páginas activas de subida
            window.dispatchEvent(
              new CustomEvent("factura:status_update", { detail: item })
            );

            if (item.status === "success" || item.status === "completed") {
              showToast(
                "success",
                "Factura procesada",
                `El archivo ${item.file} ha sido procesado exitosamente.`
              );
            } else if (item.status === "review") {
              showToast(
                "warning",
                "Factura para revisar",
                `El archivo ${item.file} requiere revisión manual.`
              );
            } else if (item.status === "error") {
              showToast(
                "error",
                "Error de procesamiento",
                `El archivo ${item.file} falló: ${item.msg}`
              );
            }
          }
        } catch {
          // ignore malformed payloads
        }
      };

      socket.onclose = () => {
        if (isCancelled) return;
        retryTimer = setTimeout(() => {
          retryDelay = Math.min(retryDelay * 2, 15000);
          connect();
        }, retryDelay);
      };

      socket.onerror = () => {
        socket?.close();
      };
    };

    connect();

    return () => {
      isCancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      socket?.close();
    };
  }, [user]);
  const isDashboard = location.pathname === "/";
  const isModulePath = /^\/(facturas|ventas|exterior|legalizacion|usuarios|empresas|nits|confecamaras|herramientas)(\/|$)/.test(location.pathname);
  const isAdminPath = /^\/(usuarios|empresas|nits)(\/|$)/.test(location.pathname);
  const isConfecamarasPath = /^\/confecamaras(\/|$)/.test(location.pathname);

  const visibleItems = (group: NavGroup) =>
    group.items.filter(
      (item) => !item.requires || isAdmin || hasPermission(item.requires),
    );

  const groups = [dashboardGroup, consultasGroup, herramientasGroup]
    .map((group) => ({ ...group, items: visibleItems(group) }))
    .filter((group) => group.items.length > 0);

  return (
    <div className="flex min-h-full flex-col bg-bg-primary">
      <a
        href="#contenido-principal"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-accent-blue focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-white"
      >
        Saltar al contenido principal
      </a>
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white shadow-card">
        <div className="mx-auto flex w-full items-center justify-between px-4 py-2 lg:px-6 lg:py-3 relative">
          <Logo to="/">
            {!isDashboard && !isModulePath && (
              <span className="hidden text-sm font-semibold text-ink-primary md:inline">
                G&D
              </span>
            )}
          </Logo>

          {!isDashboard && !isModulePath && (
            <nav
              aria-label="Navegación principal"
              className="order-3 -mx-1 flex w-full flex-wrap items-center gap-x-3 gap-y-1 overflow-x-auto px-1 lg:order-2 lg:mx-0 lg:w-auto lg:flex-nowrap lg:overflow-visible"
            >
              {groups.map((group) => (
                <div key={group.label} className="flex items-center gap-1">
                  <span className="text-xs font-semibold uppercase tracking-wide text-ink-secondary">
                    {group.label}
                  </span>
                  {group.items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.to === "/"}
                      className={({ isActive }) =>
                        clsx(
                          "rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors lg:px-3",
                          isActive
                            ? "bg-accent-blue/15 text-accent-blue"
                            : "text-ink-secondary hover:bg-slate-100",
                        )
                      }
                    >
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              ))}
            </nav>
          )}

          {!isAdminPath && !isConfecamarasPath && location.pathname !== "/herramientas" && (
            <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 flex items-center">
              <EmpresaSelector />
            </div>
          )}

          <div className="ml-auto flex items-center gap-4">
            <UserInfo
              nombre={user?.nombre}
            />
          </div>
        </div>
      </header>

      <main
        id="contenido-principal"
        tabIndex={-1}
        className="w-full flex-1 px-4 py-6 lg:px-8 xl:px-10"
      >
        <PageTransition>
          <Outlet />
        </PageTransition>
      </main>
    </div>
  );
};
